import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  Grid,
  Button,
  Typography,
  Chip,
  IconButton,
  Menu,
  MenuItem,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TableContainer,
  Avatar,
  AvatarGroup,
  TextField,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  FormControlLabel,
  Checkbox,
  Stack,
  CircularProgress,
} from '@mui/material';
import {
  Add as AddIcon,
  MoreVert as MoreVertIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  ViewModule as GridViewIcon,
  TableRows as TableViewIcon,
  ArticleOutlined as FullPageIcon,
  PictureInPictureAlt as PopupIcon,
  Close as CloseIcon,
  Search as SearchIcon,
  Sync as SyncIcon,
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import api from '../../utils/api';
import { useAuth } from '../../context/AuthContext';
import GroupDetailContent from './GroupDetailContent';
import { rbacAccentAt, sortGroupsForList } from '../../utils/rbacListUi';
import { formatModuleLabel } from '../../utils/moduleLabel';

const LAYOUT_KEY = 'erp4c.groupsListLayout';

function groupRoles(g) {
  return g.roles || g.Roles || [];
}

function groupMembers(g) {
  return g.users || g.Users || [];
}

const GroupsList = () => {
  const navigate = useNavigate();
  const { hasPermission } = useAuth();
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedGroup, setSelectedGroup] = useState(null);
  const [layout, setLayout] = useState(() => {
    try {
      const v = localStorage.getItem(LAYOUT_KEY);
      return v === 'table' ? 'table' : 'grid';
    } catch {
      return 'grid';
    }
  });
  const [viewDialogGroupId, setViewDialogGroupId] = useState(null);
  const [viewDialogTitle, setViewDialogTitle] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [moduleFilter, setModuleFilter] = useState('');
  const [granularOnly, setGranularOnly] = useState(false);
  const [moduleOptions, setModuleOptions] = useState([]);
  const [groupsTotal, setGroupsTotal] = useState(0);
  const [syncing, setSyncing] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(searchInput.trim()), 350);
    return () => clearTimeout(t);
  }, [searchInput]);

  useEffect(() => {
    api
      .get('/roles/permissions')
      .then((r) => {
        const g = r.data?.data?.grouped || {};
        setModuleOptions(Object.keys(g).sort());
      })
      .catch(() => {});
  }, []);

  const fetchGroups = useCallback(async () => {
    setLoading(true);
    try {
      const params = { limit: 500 };
      if (debouncedSearch) params.search = debouncedSearch;
      if (moduleFilter) params.module = moduleFilter;
      if (granularOnly) params.granular_only = 'true';
      const r = await api.get('/groups', { params });
      setGroups(r.data.data || []);
      setGroupsTotal(r.data.pagination?.total ?? (r.data.data || []).length);
    } catch (e) {
      console.error(e);
      toast.error(e.response?.data?.message || 'Failed to load groups');
    } finally {
      setLoading(false);
    }
  }, [debouncedSearch, moduleFilter, granularOnly]);

  useEffect(() => {
    fetchGroups();
  }, [fetchGroups]);

  const handleSyncGranularGroups = async () => {
    if (
      !window.confirm(
        'Create or update groups named like "Group - Accounts-View", each with exactly one role: the matching granular role "Role - Accounts-View"? Run App Roles → Sync granular roles first if those roles are missing. Groups with these exact names will have their role list reset to that single role.'
      )
    ) {
      return;
    }
    setSyncing(true);
    try {
      const r = await api.post('/groups/sync-granular');
      const d = r.data?.data || {};
      const skipped = d.skipped_missing_role ?? 0;
      toast.success(
        `Granular groups synced: ${d.groups_created ?? 0} created, ${d.groups_description_updated ?? 0} descriptions updated. ${skipped > 0 ? `${skipped} skipped (missing granular role).` : ''}`
      );
      fetchGroups();
    } catch (e) {
      toast.error(e.response?.data?.message || 'Sync failed');
    } finally {
      setSyncing(false);
    }
  };

  const persistLayout = useCallback((mode) => {
    setLayout(mode);
    try {
      localStorage.setItem(LAYOUT_KEY, mode);
    } catch {
      /* ignore */
    }
  }, []);

  const handleMenuOpen = (e, group) => {
    setAnchorEl(e.currentTarget);
    setSelectedGroup(group);
  };
  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedGroup(null);
  };

  const closeGroupDialog = useCallback(() => {
    setViewDialogGroupId(null);
    setViewDialogTitle('');
  }, []);

  const handleDelete = async () => {
    if (!window.confirm('Delete this group?')) {
      handleMenuClose();
      return;
    }
    try {
      await api.delete(`/groups/${selectedGroup.id}`);
      toast.success('Group deleted');
      fetchGroups();
    } catch (e) {
      toast.error(e.response?.data?.message || 'Failed to delete');
    }
    handleMenuClose();
  };

  const sortedGroups = useMemo(() => sortGroupsForList(groups), [groups]);
  const groupSeqById = useMemo(
    () => new Map(sortedGroups.map((group, idx) => [group.id, idx + 1])),
    [sortedGroups]
  );
  const hasFilters = Boolean(debouncedSearch || moduleFilter || granularOnly);

  const renderGroupCard = (group, accentIndex) => {
    const accent = rbacAccentAt(accentIndex);
    const roles = [...groupRoles(group)].sort((a, b) =>
      (a.name || '').localeCompare(b.name || '', undefined, { sensitivity: 'base' })
    );
    const members = [...groupMembers(group)].sort((a, b) => {
      const la = `${a.last_name || ''} ${a.first_name || ''}`.trim();
      const lb = `${b.last_name || ''} ${b.first_name || ''}`.trim();
      return la.localeCompare(lb, undefined, { sensitivity: 'base' });
    });
    const title = group.name || '—';
    return (
      <Card
        sx={{
          height: '100%',
          borderLeft: `5px solid ${accent.border}`,
          bgcolor: accent.bg,
          transition: 'box-shadow 0.2s ease',
          '&:hover': { boxShadow: 4 },
        }}
      >
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 1 }}>
                <Chip label={`#${String(accentIndex + 1).padStart(2, '0')}`} size="small" />
              </Box>
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                {title}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {group.description}
              </Typography>
            </Box>
            <IconButton onClick={(e) => handleMenuOpen(e, group)}>
              <MoreVertIcon />
            </IconButton>
          </Box>
          <Typography variant="subtitle2" color="text.secondary" gutterBottom>
            Roles
          </Typography>
          <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mb: 2 }}>
            {roles.map((r) => (
              <Chip key={r.id} label={r.name} size="small" color="primary" variant="outlined" />
            ))}
            {roles.length === 0 && (
              <Typography variant="caption" color="text.secondary">
                No roles assigned
              </Typography>
            )}
          </Box>
          <Typography variant="subtitle2" color="text.secondary" gutterBottom>
            Users ({members.length})
          </Typography>
          {members.length > 0 ? (
            <AvatarGroup max={5} sx={{ justifyContent: 'flex-start' }}>
              {members.map((u) => (
                <Avatar key={u.id} sx={{ width: 32, height: 32, fontSize: '0.8rem' }}>
                  {u.first_name?.[0]}
                  {u.last_name?.[0]}
                </Avatar>
              ))}
            </AvatarGroup>
          ) : (
            <Typography variant="caption" color="text.secondary">
              No users
            </Typography>
          )}
        </CardContent>
      </Card>
    );
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3, gap: 2, flexWrap: 'wrap' }}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 700 }}>
            Groups
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Manage user groups and role assignments. Use <strong>Sync granular groups</strong> for{' '}
            <strong>Group - Module-Action</strong> groups (one granular role each), after syncing granular roles.
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, ml: 'auto' }}>
          <Tooltip title="Card view">
            <IconButton
              color={layout === 'grid' ? 'primary' : 'default'}
              onClick={() => persistLayout('grid')}
              aria-label="Show groups as cards"
            >
              <GridViewIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title="Row view">
            <IconButton
              color={layout === 'table' ? 'primary' : 'default'}
              onClick={() => persistLayout('table')}
              aria-label="Show groups as rows"
            >
              <TableViewIcon />
            </IconButton>
          </Tooltip>
          {hasPermission('groups_add') && (
            <>
              <Tooltip title='Creates Group - Accounts-View style groups wired to Role - Accounts-View (sync roles first)'>
                <span>
                  <Button
                    variant="outlined"
                    startIcon={syncing ? <CircularProgress size={18} color="inherit" /> : <SyncIcon />}
                    onClick={handleSyncGranularGroups}
                    disabled={syncing}
                  >
                    Sync granular groups
                  </Button>
                </span>
              </Tooltip>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => navigate('/admin/groups/new')}
                sx={{ background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)' }}
              >
                Add Group
              </Button>
            </>
          )}
        </Box>
      </Box>

      <Card sx={{ mb: 3, p: 2 }}>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems={{ md: 'center' }} flexWrap="wrap">
          <TextField
            placeholder="Search name or description…"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            size="small"
            sx={{ minWidth: 260, flex: 1 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ color: 'text.secondary' }} />
                </InputAdornment>
              ),
            }}
          />
          <FormControl size="small" sx={{ minWidth: 220 }}>
            <InputLabel id="groups-module-filter-label">Module</InputLabel>
            <Select
              labelId="groups-module-filter-label"
              label="Module"
              value={moduleFilter}
              onChange={(e) => setModuleFilter(e.target.value)}
            >
              <MenuItem value="">
                <em>All modules</em>
              </MenuItem>
              {moduleOptions.map((m) => (
                <MenuItem key={m} value={m}>
                  {formatModuleLabel(m)}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControlLabel
            control={<Checkbox checked={granularOnly} onChange={(e) => setGranularOnly(e.target.checked)} />}
            label="Single role only"
          />
        </Stack>
        {groupsTotal > 500 && (
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1.5 }}>
            {groupsTotal} groups match; list is capped at 500. Narrow search or pick a module.
          </Typography>
        )}
      </Card>

      {loading ? (
        <Typography variant="body2" color="text.secondary">
          Loading…
        </Typography>
      ) : layout === 'grid' ? (
        <Grid container spacing={3}>
          {sortedGroups.map((group, idx) => (
            <Grid item xs={12} sm={6} md={4} key={group.id}>
              {renderGroupCard(group, idx)}
            </Grid>
          ))}
        </Grid>
      ) : (
        <Card>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ width: 72 }}>Seq</TableCell>
                  <TableCell>Name</TableCell>
                  <TableCell>Description</TableCell>
                  <TableCell align="right">Roles</TableCell>
                  <TableCell align="right">Users</TableCell>
                  <TableCell align="right" padding="checkbox" />
                </TableRow>
              </TableHead>
              <TableBody>
                {sortedGroups.map((group, rowIdx) => {
                  const accent = rbacAccentAt(rowIdx);
                  const roles = groupRoles(group);
                  const members = groupMembers(group);
                  return (
                    <TableRow
                      key={group.id}
                      hover
                      sx={{ borderLeft: `4px solid ${accent.border}`, '& > *': { bgcolor: 'transparent' } }}
                    >
                      <TableCell sx={{ fontWeight: 600 }}>{String(rowIdx + 1).padStart(2, '0')}</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>{group.name || '—'}</TableCell>
                      <TableCell sx={{ maxWidth: 360 }}>
                        <Typography variant="body2" color="text.secondary" noWrap title={group.description}>
                          {group.description || '—'}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">{roles.length}</TableCell>
                      <TableCell align="right">{members.length}</TableCell>
                      <TableCell align="right" padding="checkbox">
                        <IconButton size="small" onClick={(e) => handleMenuOpen(e, group)}>
                          <MoreVertIcon />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
          {sortedGroups.length === 0 && (
            <Box sx={{ py: 4, textAlign: 'center' }}>
              <Typography variant="body2" color="text.secondary">
                {hasFilters ? 'No groups match these filters.' : 'No groups yet.'}
              </Typography>
            </Box>
          )}
        </Card>
      )}

      {!loading && layout === 'grid' && sortedGroups.length === 0 && (
        <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
          {hasFilters ? 'No groups match these filters.' : 'No groups yet.'}
        </Typography>
      )}

      <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleMenuClose}>
        <MenuItem
          onClick={() => {
            navigate(`/admin/groups/${selectedGroup?.id}`);
            handleMenuClose();
          }}
        >
          <FullPageIcon sx={{ mr: 1, fontSize: 20 }} /> Full page
        </MenuItem>
        <MenuItem
          onClick={() => {
            const seq = selectedGroup?.id ? groupSeqById.get(selectedGroup.id) : null;
            setViewDialogGroupId(selectedGroup?.id ?? null);
            setViewDialogTitle(`${seq ? `#${String(seq).padStart(2, '0')} ` : ''}${selectedGroup?.name || ''}`);
            handleMenuClose();
          }}
        >
          <PopupIcon sx={{ mr: 1, fontSize: 20 }} /> Popup window
        </MenuItem>
        {hasPermission('groups_modify') && (
          <MenuItem
            onClick={() => {
              navigate(`/admin/groups/${selectedGroup?.id}/edit`);
              handleMenuClose();
            }}
          >
            <EditIcon sx={{ mr: 1, fontSize: 20 }} /> Edit
          </MenuItem>
        )}
        {hasPermission('groups_delete') && (
          <MenuItem onClick={handleDelete} sx={{ color: 'error.main' }}>
            <DeleteIcon sx={{ mr: 1, fontSize: 20 }} /> Delete
          </MenuItem>
        )}
      </Menu>

      <Dialog fullWidth maxWidth="md" open={Boolean(viewDialogGroupId)} onClose={closeGroupDialog}>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, pr: 1 }}>
          <Typography component="span" variant="h6" sx={{ fontWeight: 700, overflow: 'hidden', textOverflow: 'ellipsis' }}>
            {viewDialogTitle || 'Group'}
          </Typography>
          <IconButton aria-label="Close" onClick={closeGroupDialog} size="small">
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers sx={{ pt: 2 }}>
          {viewDialogGroupId && (
            <GroupDetailContent groupId={viewDialogGroupId} variant="dialog" onRequestClose={closeGroupDialog} />
          )}
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default GroupsList;
